/**
 * Dr. Mani's Magic Quiz — UX Update v2 copy.
 *
 * Source of truth: dr-mm-files/UX-dmm-quiz-ux-update-v2-for-ai - *.csv
 * (MICRO-CELEBRATION COPY, READING COUNTER, REVEAL BUILD-UP, BECAUSE LINE
 * TEMPLATES tabs). Every string here is verbatim from those tabs.
 *
 * House rules: 3rd to 5th grade reading level, no em-dashes, no exclamation
 * points, no emojis. The excitement comes from the words and the motion.
 */
import type { ProductKey } from './types';

// ===== UX-1: Micro-celebration copy banks =====

/**
 * Per-question line banks. Rotate within a bank, never repeat a line in a
 * session. When a bank is exhausted, overflow into DMM_CELEBRATION_GENERIC.
 */
export const DMM_CELEBRATION_BANKS: Record<string, string[]> = {
  q1_living: [
    'That changes everything. Truly.',
    'Good. Now we know where your garden lives.',
    'That one answer just retired forty readings.',
  ],
  q2_grows: [
    'Your garden is telling on you.',
    'Interesting mix. Not what most people pick.',
    'That tells us more than you think.',
  ],
  q3_setup: [
    'Noted. And a little revealing.',
    'The dirt is starting to talk.',
  ],
  q4_lawn_size: [
    'Good. Your amounts will fit your yard exactly.',
  ],
  q5_size: [
    'Sized. Nothing wasted, nothing short.',
    'Your plan is being measured to fit.',
  ],
  q6_why: [
    'Honest answer. The best kind.',
    'That reason says more about you than the other eight answers.',
    'Your why just cut the field in half. Twice.',
  ],
  q7_struggle: [
    'None of that was your fault. You will see.',
    'Every box you checked is a clue, not a confession.',
  ],
  q8_experience: [
    'Almost there. Two answers from your reading.',
    'However long you have grown, the reading meets you there.',
  ],
  q9_proudest: [
    'That was the last piece.',
    'Done. Hold still while we read you back.',
  ],
};

/** Generic overflow bank, used when a question's own bank is exhausted. */
export const DMM_CELEBRATION_GENERIC: string[] = [
  'Good pick.',
  'We can work with that.',
  'That helps more than you know.',
  'Your reading is taking shape.',
];

/**
 * Pick a celebration line for a question, never repeating a line in a session.
 * Question bank first, generic bank as overflow. Mutates nothing; the caller
 * records the returned line in its used set.
 *
 * Why this matters: the counter narration for Q6 repeats a Q6 bank line
 * verbatim ("Your why just cut the field in half. Twice."). Callers seed the
 * used set with the counter narration lines so the same words never show in
 * two places at once.
 */
export function pickDmmCelebrationLine(questionId: string, used: ReadonlySet<string>): string {
  const bank = DMM_CELEBRATION_BANKS[questionId] || [];
  const fresh = bank.find((line) => !used.has(line));
  if (fresh) return fresh;
  const generic = DMM_CELEBRATION_GENERIC.find((line) => !used.has(line));
  if (generic) return generic;
  // Every line is spent (only possible after heavy back-and-forth). Reuse the
  // question's own bank rather than going silent.
  return bank[0] || DMM_CELEBRATION_GENERIC[0];
}

// ===== UX-5: Reading counter schedule + narration =====

export interface DmmCounterStep {
  /** Count on the table after this question is answered. */
  count: number;
  /** One-line narration shown under the counter. */
  narration: string;
}

/** The counter opens on this before any answer. */
export const DMM_COUNTER_START: DmmCounterStep = {
  count: 300,
  narration: 'Three hundred gardeners walk in. One walks out with your name on it.',
};

/**
 * Count after each answered question. The lawn path includes q4_lawn_size;
 * the apartment path spreads that drop across Q3 and Q5 so every answered
 * question still ticks down. The count must land on exactly 1 at Q9.
 */
export function getDmmCounterSchedule(hasLawn: boolean): Record<string, DmmCounterStep> {
  return {
    q1_living: { count: 260, narration: 'Your first answer just retired forty readings.' },
    q2_grows: { count: 210, narration: 'What you grow is narrowing the field fast.' },
    q3_setup: {
      count: hasLawn ? 170 : 160,
      narration: 'More than a third are gone. Yours is still in.',
    },
    ...(hasLawn
      ? { q4_lawn_size: { count: 150, narration: 'Your yard just sent another twenty home.' } }
      : {}),
    q5_size: { count: 120, narration: 'Sized. The field keeps shrinking.' },
    q6_why: { count: 60, narration: 'Your why just cut the field in half. Twice.' },
    q7_struggle: { count: 12, narration: 'Now it is getting personal.' },
    q8_experience: { count: 3, narration: 'Three readings left. They all sound like you.' },
    q9_proudest: { count: 1, narration: 'One reading left. It has your name on it.' },
  };
}

/** Chip label for a given count ("300 readings on the table" ... "1 reading, locked"). */
export function dmmCounterLabel(count: number): string {
  if (count === 1) return '1 reading, locked';
  return `${count} readings on the table`;
}

// ===== UX-6: Pre-reveal build-up sequence =====

export interface DmmBuildUpLine {
  line: string;
  /** Seed-mark growth stage (1 to 5) reached while this line shows. */
  stage: 1 | 2 | 3 | 4 | 5;
}

/**
 * Four lines fade in one at a time, about 1 second each, over a seed mark
 * growing stage by stage (five stages, matching the five-seed reading).
 * Line 3 covers stages three and four (a sprout, then leaves).
 */
export const DMM_BUILD_UP_LINES: DmmBuildUpLine[] = [
  { line: 'Reading your answers.', stage: 1 },
  { line: 'Checking them against the three things every plant needs.', stage: 2 },
  { line: 'Your one missing thing, found.', stage: 4 },
  { line: 'And your gardener type is ready.', stage: 5 },
];

// ===== UX-2: Because line templates =====

export interface DmmBecauseTemplate {
  id: string;
  /** The clause after "Because": full line renders as `Because {clause}, {payoff}.` */
  clause: string;
  payoff: string;
}

/** Render a template as its full verbatim line. */
export function dmmBecauseLine(template: DmmBecauseTemplate): string {
  return `Because ${template.clause}, ${template.payoff}.`;
}

/**
 * Hero stacking rule: combine the strongest struggle clause with the setup or
 * why clause into one sentence where it reads naturally, e.g. "Because your
 * plants kept dying in dead dirt and you grow to feed your people, the life
 * comes first."
 */
export function dmmBecauseLineStacked(
  primary: DmmBecauseTemplate,
  secondary: DmmBecauseTemplate
): string {
  return `Because ${primary.clause} and ${secondary.clause}, ${primary.payoff}.`;
}

/** Struggle-triggered templates, keyed by the struggle answer values that fire them. */
export const DMM_BECAUSE_STRUGGLE: Array<{
  trigger: string[];
  card: ProductKey;
  template: DmmBecauseTemplate;
}> = [
  {
    trigger: ['plants_die'],
    card: 'plant_super_boost',
    template: {
      id: 'struggle_plants_die',
      clause: 'your plants kept dying in dead dirt',
      payoff: 'the life comes first',
    },
  },
  {
    trigger: ['yellow_pale'],
    card: 'crab_kelp_amino_acids',
    template: {
      id: 'struggle_yellow_pale',
      clause: 'your leaves went pale and hungry',
      payoff: 'real food comes first',
    },
  },
  {
    trigger: ['no_bloom_fruit'],
    card: 'crab_kelp_amino_acids',
    template: {
      id: 'struggle_no_bloom_fruit',
      clause: 'you got leaves with no payoff',
      payoff: 'the food that feeds blooms comes first',
    },
  },
  {
    trigger: ['rot_soggy', 'ugly_mix'],
    card: 'super_soil',
    template: {
      id: 'struggle_rot_soggy',
      clause: 'your roots were drowning in a mix that rots',
      payoff: 'a home that breathes comes first',
    },
  },
];

/** Profile-triggered templates (both point at the bundle). */
export const DMM_BECAUSE_PROFILE: Record<string, DmmBecauseTemplate> = {
  'The Whole-Garden Builder': {
    id: 'profile_whole_garden',
    clause: 'more than one thing was missing',
    payoff: 'the whole system together is the smart start',
  },
  'The Reviver': {
    id: 'profile_reviver',
    clause: 'you will not quit on that plant',
    payoff: 'it gets the full rescue: life, food, and a home',
  },
};

/** has_lawn and lawn is the main thing they grow -> Plant Super Boost. */
export const DMM_BECAUSE_LAWN: DmmBecauseTemplate = {
  id: 'lawn_main',
  clause: 'a great lawn is grown from the soil up',
  payoff: 'the life goes on the grass first',
};

/** setup includes containers or indoor pots -> Super Soil. */
export const DMM_BECAUSE_CONTAINERS: DmmBecauseTemplate = {
  id: 'setup_containers',
  clause: 'in a pot the soil is the whole world',
  payoff: 'the home matters most',
};

/** why = grow_food -> stack on the hero or use the first open card slot. */
export const DMM_BECAUSE_GROW_FOOD: DmmBecauseTemplate = {
  id: 'why_grow_food',
  clause: 'you grow to feed your people',
  payoff: 'everything here is clean enough to hand a child',
};

/** why = natural -> any card. */
export const DMM_BECAUSE_NATURAL: DmmBecauseTemplate = {
  id: 'why_natural',
  clause: 'you do it the natural way',
  payoff: 'there is nothing in this you would hide from your kids',
};

/** Fallback (no strong signal) -> the hero product. */
export const DMM_BECAUSE_FALLBACK: DmmBecauseTemplate = {
  id: 'fallback',
  clause: 'of what you grow and how you grow it',
  payoff: 'this is where your garden starts',
};
